import React, { useEffect, useState } from "react";
import { api, getToken } from "@/api/client.js";
import { useMarcelMode } from "@/context/MarcelMode.jsx";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";
import { pointNames } from "@/lib/pointNames.js";

const COPY = {
  fr: {
    title: "Mise à jour des points du groupe",
    deposit: "Déposer mes points",
    download: "Installer la carte du groupe",
    add: "Ajouter mon fichier KML",
    none: "Aucun nouveau point aujourd’hui",
    invalid: "Fichier non valide — aucun point trouvé",
    received: "Fichier reçu ✅",
    waiting: "À recevoir",
    noFile: "Pas de fichier aujourd’hui",
    create: "Créer la nouvelle carte du groupe",
    publish: "Publier la nouvelle version",
    confirm: "Confirmer la Version",
    installed: "J’ai installé la Version",
    newMap: "Nouvelle carte disponible",
    points: "points",
    fresh: "nouveaux",
    history: "Versions",
    files: "Fichiers reçus",
    news: "Points nouveaux",
    dups: "Doublons",
    doubt: "À vérifier par Marcel",
    total: "Total",
    keep: "Garder",
    dup: "Doublon",
    drop: "Exclure",
    remove: "Retirer le fichier",
    author: "Auteur",
    base: "Points déjà sur la carte",
    emptyPublish: "Aucun nouveau point à publier",
    today: "Nouveaux points aujourd’hui ?",
  },
  en: {
    title: "Group point update",
    deposit: "Drop my points",
    download: "Download the group map",
    add: "Add my KML file",
    none: "No new point today",
    invalid: "Invalid file — no point found",
    received: "File received ✅",
    waiting: "Still waiting",
    noFile: "No file today",
    create: "Create the new group map",
    publish: "Publish the new version",
    confirm: "Confirm Version",
    installed: "I installed Version",
    newMap: "New map available",
    points: "points",
    fresh: "new",
    history: "Versions",
    files: "Files received",
    news: "New points",
    dups: "Duplicates",
    doubt: "Marcel needs to check",
    total: "Total",
    keep: "Keep",
    dup: "Duplicate",
    drop: "Exclude",
    remove: "Remove the file",
    author: "Author",
    base: "Points already on the map",
    emptyPublish: "No new point to publish",
    today: "New points today?",
  },
};

async function send(path, payload, method = "POST") {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const options = { method, headers };
  if (payload instanceof FormData) {
    options.body = payload;
  } else if (payload) {
    headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(payload);
  }
  const res = await fetch(path, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = typeof data.detail === "string" ? data.detail : (data.detail?.message || "");
    throw new Error(detail || `api_${res.status}`);
  }
  return data;
}

function dayHeading(iso) {
  const months = ["JANVIER", "FÉVRIER", "MARS", "AVRIL", "MAI", "JUIN", "JUILLET", "AOÛT", "SEPTEMBRE", "OCTOBRE", "NOVEMBRE", "DÉCEMBRE"];
  const parts = String(iso || "").split("-");
  if (parts.length !== 3) return "";
  return `${Number(parts[2])} ${months[Number(parts[1]) - 1] || ""}`;
}

function collectLine(hunter) {
  if (hunter.status === "none") return `${hunter.first_name} ✓ aucun`;
  if (hunter.status === "received") {
    const count = hunter.point_count || 0;
    return `${hunter.first_name} ✓ ${count} point${count > 1 ? "s" : ""}`;
  }
  return `${hunter.first_name} ⏳ en attente`;
}

async function saveFile(code, ext) {
  const token = getToken();
  const res = await fetch(`/api/nordquest/group/file/${code}/${ext}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("download");
  const blob = await res.blob();
  const named = (res.headers.get("content-disposition") || "").match(/filename="([^"]+)"/);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = named?.[1] || `Orignal2026_Groupe_V${code}.${ext}`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function GroupMap({ mode = "full" }) {
  const { actingAsAdmin } = useMarcelMode();
  const { lang } = useOrignalUi();
  const t = COPY[lang] || COPY.fr;
  const [group, setGroup] = useState(null);
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [hold, setHold] = useState(false);
  const [found, setFound] = useState(null);

  async function reload() {
    const body = await api("/api/nordquest/group");
    if (body && Array.isArray(body.hunters)) setGroup(body);
  }

  useEffect(() => {
    reload().catch(() => {});
  }, []);

  async function onFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const text = await file.text();
    const names = pointNames(text);
    if (!names.length) {
      setFound(null);
      setNote(t.invalid);
      return;
    }
    setFound({ file, names });
    setNote("");
  }

  async function sendFound() {
    if (!found?.file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", found.file);
      const result = await send("/api/nordquest/group/file", body);
      const count = Number(result.point_count || found.names.length || 0);
      setFound(null);
      setNote(`${count} nouveaux points trouvés ✓`);
      await reload();
    } catch (err) {
      setNote(err.message && err.message.startsWith("api_") ? t.invalid : (err.message || t.invalid));
    } finally {
      setBusy(false);
    }
  }

  async function markNone() {
    setBusy(true);
    try {
      const result = await send("/api/nordquest/group/none", {});
      setNote(result.message || t.noFile);
      await reload();
    } catch {
      setNote(t.invalid);
    } finally {
      setBusy(false);
    }
  }

  async function closeDay(force) {
    setBusy(true);
    try {
      await send("/api/nordquest/group/close", { force });
      setHold(false);
      setNote("");
      await reload();
    } catch (err) {
      setHold(true);
      setNote(err.message || "");
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    setBusy(true);
    try {
      const result = await send("/api/nordquest/group/publish", {});
      setNote(`${t.confirm} ${result.code}`);
      setOpen(false);
      await reload();
    } catch (err) {
      setNote(err.message || t.emptyPublish);
    } finally {
      setBusy(false);
    }
  }

  const latest = group?.latest;
  const showNew = latest && latest.code !== "1" && group.mine_installed !== latest.code;
  const preview = group?.preview;

  if (mode === "versions") {
    return (
      <section className="og-groupmap" data-testid="group-map" data-surface="dark">
        <h3>{t.history}</h3>
        <ul data-testid="kml-history">
          {(group?.versions || []).map((row) => (
            <li key={row.code}>
              <button type="button" className="og-tile" onClick={() => saveFile(row.code, "kml")}>
                V{row.code} · {row.date} · {row.point_count} {t.points}{row.code === "1" ? "" : ` · +${row.new_count}`}
              </button>
            </li>
          ))}
        </ul>
        <h3>Qui a installé</h3>
        <ul data-testid="kml-installed-list">
          {(group?.hunters || []).map((hunter) => (
            <li key={hunter.member_id}>
              <span>{hunter.first_name}</span>
              <span>V{hunter.installed || "—"} {hunter.installed_mark === "ok" ? "✅" : ""}</span>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  const mine = (group?.hunters || []).find((hunter) => hunter.member_id === group?.mine);
  const evening = mode === "return";

  return (
    <section className="og-groupmap" data-testid="group-map" data-surface="dark">
      {evening ? (
        <>
          <h3 data-testid="collect-title">COLLECTE DES POINTS — {dayHeading(group?.today)}</h3>
          {mine?.status === "waiting" ? (
            <div data-testid="collect-ask">
              <p>AS-TU CRÉÉ DES NOUVEAUX POINTS AUJOURD’HUI ?</p>
              <button type="button" className="og-btn og-btn--primary" data-testid="collect-yes" onClick={() => setSending(true)}>Oui — envoyer mes nouveaux points</button>
              <button type="button" className="og-btn" data-testid="collect-no" disabled={busy} onClick={markNone}>Non — aucun nouveau point</button>
            </div>
          ) : null}
          {mine?.status === "none" ? <p data-testid="collect-none">Aucun nouveau point aujourd’hui ✓</p> : null}
          {mine?.status === "received" ? (
            <p data-testid="collect-received">Fichier reçu ✓ {mine.point_count} nouveau{mine.point_count > 1 ? "x" : ""} point{mine.point_count > 1 ? "s" : ""} détecté{mine.point_count > 1 ? "s" : ""}</p>
          ) : null}
          {sending && mine?.status === "waiting" ? (
            <>
              <ol data-testid="collect-steps">
                <li>Dans NordQuest : préparer mes nouveaux points</li>
                <li>Revenir ici : choisir mon fichier</li>
                <li>Envoyer mes points au groupe</li>
              </ol>
              <details>
                <summary>Options avancées</summary>
                <p>NordQuest peut remettre un fichier KML ou GPX. Le nom du format reste ici.</p>
              </details>
            </>
          ) : null}
          <ul data-testid="collect-board">
            {(group?.hunters || []).map((hunter) => (
              <li key={hunter.member_id} data-testid={`collect-${hunter.member_id}`}>{collectLine(hunter)}</li>
            ))}
          </ul>
        </>
      ) : <h3>{t.title}</h3>}
      {(!evening || sending) ? (
        <label className="og-btn og-btn--primary og-groupmap__file">
          {evening ? "Choisir mon fichier" : t.add}
          <input data-testid="kml-file" type="file" accept=".kml,.kmz" disabled={busy} onChange={onFile} />
        </label>
      ) : null}
      {!evening ? <button type="button" className="og-btn" data-testid="kml-none" disabled={busy} onClick={markNone}>{t.none}</button> : null}
      {found ? (
        <div data-testid="collect-found">
          <p>{found.names.length} nouveaux points trouvés ✓</p>
          <ul>{found.names.map((name) => <li key={name}>{name}</li>)}</ul>
          <button type="button" className="og-btn og-btn--primary" data-testid="collect-send" disabled={busy} onClick={sendFound}>
            Envoyer mes {found.names.length} points au groupe
          </button>
        </div>
      ) : null}
      {note ? <p data-testid="kml-note">{note}</p> : null}
      {mode === "full" ? (
        <ul className="og-groupmap__people" data-testid="kml-hunters">
          {(group?.hunters || []).map((hunter) => (
            <li key={hunter.member_id}>
              <span>{hunter.first_name}</span>
              <span>{hunter.label}{hunter.status === "received" ? ` — ${hunter.point_count}` : ""}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {(group?.reminders || []).slice(0, 6).map((line) => (
        <p key={line} className="og-hint" data-testid="kml-reminder">{line}</p>
      ))}
      {showNew ? (
        <div data-testid="kml-available">
          <p>NOUVEAUX POINTS DISPONIBLES</p>
          <p>Carte V{latest.code}</p>
          <p>{latest.new_count} nouveaux points depuis hier</p>
          <p>La carte complète est envoyée, avec les anciens points et les nouveaux.</p>
          <button type="button" className="og-btn og-btn--primary" data-testid="kml-download" onClick={() => saveFile(latest.code, "kml").catch(() => setNote(t.invalid))}>
            Installer les nouveaux points
          </button>
          <button type="button" className="og-btn" data-testid="kml-installed" disabled={busy} onClick={() => send("/api/nordquest/group/installed", { code: latest.code }).then(reload)}>
            Je vois les nouveaux points ✓
          </button>
        </div>
      ) : (
        <button type="button" className="og-btn" data-testid="kml-download" onClick={() => saveFile(latest?.code || "1", "kml").catch(() => setNote(t.invalid))}>
          {t.download}
        </button>
      )}
      {evening && actingAsAdmin && group?.is_admin ? (
        <div data-testid="collect-admin">
          {!group.collection_closed ? (
            <button type="button" className="og-btn" data-testid="collect-close" disabled={busy} onClick={() => closeDay(false)}>Fermer la collecte</button>
          ) : null}
          {hold ? (
            <div data-testid="collect-hold">
              <p>{note}</p>
              <button type="button" className="og-btn" data-testid="collect-wait" onClick={() => setHold(false)}>Attendre</button>
              <button type="button" className="og-btn" data-testid="collect-force" disabled={busy} onClick={() => closeDay(true)}>Fermer quand même</button>
            </div>
          ) : null}
          {group.collection_closed ? (
            <div data-testid="collect-prepare">
              <button type="button" className="og-btn og-btn--primary" onClick={() => setOpen(true)}>PRÉPARER LA NOUVELLE CARTE DU GROUPE</button>
              {open && preview ? (
                <div>
                  <p>Nouveaux points : {preview.new_count}</p>
                  <p>Doublons : {(preview.duplicates || []).length}</p>
                  <p>À vérifier : {preview.unresolved || 0}</p>
                  <button type="button" className="og-btn og-btn--primary" data-testid="kml-publish" disabled={busy || preview.unresolved} onClick={publish}>
                    Publier V{preview.next_code}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          <ul data-testid="collect-versions">
            {(group.hunters || []).map((hunter) => (
              <li key={hunter.member_id}>
                {hunter.first_name} {hunter.installed_mark === "ok" ? "✓" : "⏳"} V{hunter.installed || "1"}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {mode === "full" ? (
        <>
          <h3>{t.history}</h3>
          <ul data-testid="kml-history">
            {(group?.versions || []).map((row) => (
              <li key={row.code}>
                <button type="button" className="og-tile" onClick={() => saveFile(row.code, "kml")}>
                  V{row.code} · {row.date} · {row.point_count} {t.points}{row.code === "1" ? "" : ` · +${row.new_count}`}
                </button>
              </li>
            ))}
          </ul>
          <h3>Carte installée</h3>
          <ul data-testid="kml-installed-list">
            {(group?.hunters || []).map((hunter) => (
              <li key={hunter.member_id}>
                <span>{hunter.first_name}</span>
                <span>V{hunter.installed} {hunter.installed_mark === "ok" ? "✅" : "⚠️"}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {actingAsAdmin && group?.is_admin && mode === "full" ? (
        <div data-testid="kml-admin">
          <button type="button" className="og-btn og-btn--primary" onClick={() => setOpen(true)}>{t.create}</button>
          {open && preview ? (
            <div data-testid="kml-preview">
              <p>{t.files}</p>
              <ul>
                {(group.hunters || []).filter((hunter) => hunter.status === "received").map((hunter) => (
                  <li key={hunter.member_id}>
                    {hunter.first_name} — {hunter.point_count} {t.points}
                    <button type="button" onClick={() => send(`/api/nordquest/group/file/${hunter.member_id}`, null, "DELETE").then(reload)}>{t.remove}</button>
                    <label>{t.author}
                      <select value={hunter.author_id || hunter.member_id} onChange={(event) => send(`/api/nordquest/group/file/${hunter.member_id}`, { author_id: event.target.value }, "PATCH").then(reload)}>
                        {(group.hunters || []).map((person) => <option key={person.member_id} value={person.member_id}>{person.first_name}</option>)}
                      </select>
                    </label>
                  </li>
                ))}
              </ul>
              <p>{t.news} · {preview.new_count}</p>
              <ul>
                {(preview.new_points || []).map((point) => (
                  <li key={point.key}>{point.name} — {point.author}
                    <button type="button" onClick={() => send("/api/nordquest/group/decision", { point_key: point.key, action: "exclude" }).then(reload)}>{t.drop}</button>
                  </li>
                ))}
              </ul>
              <p>{t.dups} · {(preview.duplicates || []).length}</p>
              <ul>
                {(preview.duplicates || []).map((point) => <li key={point.key}>{point.name} — {point.author}</li>)}
              </ul>
              <p>{t.doubt}</p>
              <ul>
                {(preview.ambiguous || []).map((point) => (
                  <li key={point.key}>{point.name} — {point.author}
                    <button type="button" onClick={() => send("/api/nordquest/group/decision", { point_key: point.key, action: "keep" }).then(reload)}>{t.keep}</button>
                    <button type="button" onClick={() => send("/api/nordquest/group/decision", { point_key: point.key, action: "duplicate" }).then(reload)}>{t.dup}</button>
                    <button type="button" onClick={() => send("/api/nordquest/group/decision", { point_key: point.key, action: "exclude" }).then(reload)}>{t.drop}</button>
                  </li>
                ))}
              </ul>
              <p>{t.base}</p>
              <ul>
                {(preview.official || []).map((point) => (
                  <li key={point.key}>{point.name} — {point.author}
                    <button type="button" onClick={() => send("/api/nordquest/group/decision", { point_key: point.key, action: "exclude" }).then(reload)}>{t.drop}</button>
                  </li>
                ))}
              </ul>
              <p>{t.total} · {preview.total} {t.points} · {preview.new_count} {t.fresh}</p>
              <button type="button" className="og-btn og-btn--primary" data-testid="kml-publish" disabled={busy} onClick={publish}>
                {t.confirm} {preview.next_code}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
